import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DisputeApproval } from './DisputeApproval';
import { SupportInquiries } from './SupportInquiries';
import { DisputeForm } from './DisputeForm';
import { DisputeHistory } from './DisputeHistory';
import { PayrollDueReminder } from './PayrollDueReminder';
import { useFirebase } from '../contexts/FirebaseContext';
import { getDisputes, getDisputesByEmail } from '../services/disputeService';
import { isFinancialDispute, isSupportInquiry } from '../lib/disputeUtils';
import { DollarSign, HelpCircle, PlusCircle, History } from 'lucide-react';

interface Props {
  tenantId: string;
  isAdminOrManager: boolean;
}

export const DisputesTabs: React.FC<Props> = ({ tenantId, isAdminOrManager }) => {
  const { user } = useFirebase();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const requestedTab = queryParams.get('tab');
  
  const [activeTab, setActiveTab] = useState<string>(
    requestedTab || (isAdminOrManager ? 'review' : 'inquiries')
  );
  const [prefillTip, setPrefillTip] = useState<any>(null);
  const [financialCount, setFinancialCount] = useState<number>(0);
  const [inquiryCount, setInquiryCount] = useState<number>(0);

  const fetchCounts = async () => {
    if (!tenantId) return;
    try {
      let disputes = [];
      if (isAdminOrManager) {
        disputes = await getDisputes(tenantId);
      } else if (user?.email) {
        disputes = await getDisputesByEmail(tenantId, user.email);
      }
      const pending = disputes.filter(d => d.status === 'Pending');
      const fin = pending.filter(d => isFinancialDispute(d)).length;
      const inq = pending.filter(d => isSupportInquiry(d)).length;
      setFinancialCount(fin);
      setInquiryCount(inq);
    } catch (err) {
      console.error('Error fetching dispute counts:', err);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, [tenantId, isAdminOrManager, user?.email, activeTab]);

  if (!tenantId) {
    return <div className="p-4 text-red-500">Error: Missing tenant context.</div>;
  }

  const handleOpenFinancialDispute = (prefillData?: any) => {
    if (prefillData) {
      setPrefillTip(prefillData);
    }
    setActiveTab('new_dispute');
  };

  const tabs = isAdminOrManager
    ? [
        {
          id: 'review',
          label: 'Financial Tip Corrections',
          icon: DollarSign,
          badge: financialCount,
          badgeColor: 'bg-emerald-500 text-white',
          component: (
            <DisputeApproval 
              tenantId={tenantId} 
              onSwitchToInquiries={() => setActiveTab('inquiries')} 
            />
          ),
        },
        {
          id: 'inquiries',
          label: 'Support Inquiries',
          icon: HelpCircle,
          badge: inquiryCount,
          badgeColor: 'bg-blue-500 text-white',
          component: (
            <SupportInquiries
              tenantId={tenantId}
              isAdminOrManager={true}
              onOpenFinancialDispute={handleOpenFinancialDispute}
            />
          ),
        },
        {
          id: 'new_dispute',
          label: 'Submit Tip Dispute',
          icon: PlusCircle,
          component: <DisputeForm tenantId={tenantId} prefillTip={prefillTip} />,
        },
        {
          id: 'history',
          label: 'History & Logs',
          icon: History,
          component: (
            <DisputeHistory
              tenantId={tenantId}
              userEmail={user?.email || ''}
              isAdminOrManager={true}
            />
          ),
        },
      ]
    : [
        {
          id: 'inquiries',
          label: 'Active Inquiries & Missing Shifts',
          icon: HelpCircle,
          badge: inquiryCount,
          badgeColor: 'bg-blue-500 text-white',
          component: (
            <SupportInquiries
              tenantId={tenantId}
              isAdminOrManager={false}
              onOpenFinancialDispute={handleOpenFinancialDispute}
            />
          ),
        },
        {
          id: 'history',
          label: 'Disputes & Inquiries History',
          icon: History,
          component: (
            <DisputeHistory
              tenantId={tenantId}
              userEmail={user?.email || ''}
              isAdminOrManager={false}
            />
          ),
        },
      ];

  return (
    <div className="space-y-6">
      <PayrollDueReminder />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Disputes & Support Inquiries</h1>
          <p className="text-xs text-gray-500 font-medium mt-0.5">
            Financial adjustments and support requests are cleanly separated for streamlined review.
          </p>
        </div>
      </div>

      <div className="flex border-b border-gray-200 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 scroll-smooth">
        <div className="flex space-x-6 min-w-max pr-8 md:pr-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id !== 'new_dispute') {
                    setPrefillTip(null);
                  }
                }}
                className={`pb-4 font-semibold text-sm transition-all relative flex items-center gap-2 ${
                  isActive ? 'text-blue-600 font-bold' : 'text-gray-400 hover:text-gray-600 font-medium'
                }`}
              >
                {Icon && <Icon size={16} className={isActive ? 'text-blue-600' : 'text-gray-400'} />}
                <span>{tab.label}</span>
                {typeof tab.badge === 'number' && tab.badge > 0 && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                      tab.badgeColor || 'bg-blue-600 text-white'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        {tabs.find((t) => t.id === activeTab)?.component}
      </div>
    </div>
  );
};

